export type CalculatorRunResult =
  | { status: 'OK'; emissions: unknown }
  | { status: 'INVALID_INPUT'; message: string; issues: { path: string; message: string }[] }
  | { status: 'ERROR'; message: string };

type Runner = (calculator: string, input: unknown) => CalculatorRunResult;

const OPTIONS = { disableMetrics: true, organisation: 'AIA doc site' };

/**
 * Calculator packages for each docs version that can run requests in the browser. They are loaded on demand,
 * as each one bundles every calculator along with its constants.
 */
const runnerLoaders: Record<string, () => Promise<Runner>> = {
  '4.0.2': async () => {
    const { calculateEmissions, isValidCalculatorName } = await import(
      '@aginnovationaustralia/emissions-calculators4.0.2'
    );
    return (calculator, input) => {
      if (!isValidCalculatorName(calculator)) {
        return { status: 'ERROR', message: `The ${calculator} calculator is not available in this package` };
      }
      const result = calculateEmissions(calculator, input, OPTIONS);
      if (result.status === 'OK') return { status: 'OK', emissions: result.emissions };
      if (result.status === 'INVALID_INPUT') {
        return { status: 'INVALID_INPUT', message: result.message, issues: result.issues };
      }
      return { status: 'ERROR', message: result.error.message };
    };
  },
};

export const canRunCalculator = (version: string | undefined) => !!version && version in runnerLoaders;

export async function runCalculator(
  version: string | undefined,
  calculator: string,
  input: unknown,
): Promise<CalculatorRunResult> {
  const load = version ? runnerLoaders[version] : undefined;
  if (!load) return { status: 'ERROR', message: `Requests cannot be executed for version ${version} yet` };

  try {
    const run = await load();
    return run(calculator, input);
  } catch (error) {
    console.error(`Error running the ${calculator} calculator`, error);
    return { status: 'ERROR', message: error instanceof Error ? error.message : String(error) };
  }
}
